import { getContrastColor } from '@/lib/colorUtils';
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Eye, EyeOff, Trash2, RefreshCw, Settings, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { AddCompDialog } from "./AddCompDialog";
import { CompSettingsDialog } from "./CompSettingsDialog";
import { ZillowIcon } from "@/components/icons/ZillowIcon";
import { RichTextEditor } from "@/components/RichTextEditor";
import { logger } from "@/lib/logger";

interface Comp {
  id: string;
  address: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  price: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  square_feet: number | null;
  price_per_sqft: number | null;
  status: string | null;
  days_on_market: number | null;
  visible: boolean;
  zillow_url: string | null;
}

interface CompsManagerProps {
  northId: string;
  isPublished?: boolean;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  onUpdate?: () => void; // Callback to refresh parent component data
}

export const CompsManager = ({ northId, isPublished = false, primaryColor, secondaryColor, onUpdate }: CompsManagerProps) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [northAddress, setNorthAddress] = useState("");
  const [showOnReport, setShowOnReport] = useState(false);
  const [compsSummary, setCompsSummary] = useState("");
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [northProperty, setNorthProperty] = useState<any>(null);
  const queryClient = useQueryClient();

  const { data: comps, isLoading } = useQuery({
    queryKey: ['comps', northId],
    queryFn: async () => {
      // Get north address, visibility setting, and summary
      const { data: northData } = await supabase
        .from('norths')
        .select('address, city, state, zip, price, show_comps_on_report, comps_summary, updated_at')
        .eq('id', northId)
        .single();

      if (northData) {
        setNorthAddress(`${northData.address}, ${northData.city}, ${northData.state} ${northData.zip}`);
        setNorthProperty(northData);
        if (northData.show_comps_on_report !== null && northData.show_comps_on_report !== undefined) {
          setShowOnReport(northData.show_comps_on_report);
        }
        if (northData.comps_summary) {
          setCompsSummary(northData.comps_summary);
        }
      }

      const { data, error } = await supabase
        .from('comparable_properties')
        .select('*')
        .eq('north_id', northId);

      if (error) throw error;

      // Sort in JavaScript: SOLD first, then by price descending
      const sortedData = (data || []).sort((a, b) => {
        // First sort by status (SOLD comes first)
        const aIsSold = a.status?.toUpperCase().includes('SOLD') ? 1 : 0;
        const bIsSold = b.status?.toUpperCase().includes('SOLD') ? 1 : 0;

        if (aIsSold !== bIsSold) {
          return bIsSold - aIsSold; // SOLD (1) comes before non-SOLD (0)
        }

        // Then sort by price descending
        const aPrice = a.price || 0;
        const bPrice = b.price || 0;
        return bPrice - aPrice;
      });

      return sortedData as Comp[];
    },
  });

  // Fetch comp settings for filter violation detection
  const { data: compSettings } = useQuery({
    queryKey: ['comp-settings', northId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('comp_settings')
        .select('*')
        .eq('north_id', northId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
  });

  // Evaluate which criteria a comp violates (for badges)
  const getCompViolations = (comp: Comp): string[] => {
    if (!compSettings || !northProperty) {
      return [];
    }

    const violations: string[] = [];

    // Check location filters
    if (compSettings.same_state && comp.state !== northProperty.state) {
      violations.push(`Different state`);
    }
    if (compSettings.same_city && comp.city !== northProperty.city) {
      violations.push(`Different city`);
    }
    if (compSettings.same_zip && comp.zip !== northProperty.zip) {
      violations.push(`Different ZIP`);
    }

    // Check price range (if set)
    if (compSettings.price_range_percentage && northProperty.price && comp.price) {
      const rangePercent = compSettings.price_range_percentage / 100;
      const minPrice = northProperty.price * (1 - rangePercent);
      const maxPrice = northProperty.price * (1 + rangePercent);

      if (comp.price < minPrice || comp.price > maxPrice) {
        violations.push(`Outside price range`);
      }
    }

    // Check bedrooms (if min/max set)
    if (compSettings.min_bedrooms && comp.bedrooms && comp.bedrooms < compSettings.min_bedrooms) {
      violations.push(`< ${compSettings.min_bedrooms} beds`);
    }
    if (compSettings.max_bedrooms && comp.bedrooms && comp.bedrooms > compSettings.max_bedrooms) {
      violations.push(`> ${compSettings.max_bedrooms} beds`);
    }

    // Check bathrooms (if min/max set)
    if (compSettings.min_bathrooms && comp.bathrooms && comp.bathrooms < compSettings.min_bathrooms) {
      violations.push(`< ${compSettings.min_bathrooms} baths`);
    }
    if (compSettings.max_bathrooms && comp.bathrooms && comp.bathrooms > compSettings.max_bathrooms) {
      violations.push(`> ${compSettings.max_bathrooms} baths`);
    }

    return violations;
  };

  const deleteComp = useMutation({
    mutationFn: async (compId: string) => {
      const { error } = await supabase
        .from('comparable_properties')
        .delete()
        .eq('id', compId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comps', northId] });
      toast.success('Comparable property deleted');
    },
    onError: (error) => {
      logger.error('Error deleting comp:', error);
      toast.error('Failed to delete comparable');
    },
  });

  const toggleVisibility = useMutation({
    mutationFn: async ({ compId, currentlyVisible }: { compId: string; currentlyVisible: boolean }) => {
      // If trying to make visible, check if we already have 5 visible
      if (!currentlyVisible) {
        const visibleCount = comps?.filter(c => c.visible && c.id !== compId).length || 0;
        if (visibleCount >= 5) {
          throw new Error('Maximum of 5 comps can be visible on the report');
        }
      }

      const { error } = await supabase
        .from('comparable_properties')
        .update({ visible: !currentlyVisible })
        .eq('id', compId);

      if (error) throw error;
    },
    onSuccess: (_, { currentlyVisible }) => {
      queryClient.invalidateQueries({ queryKey: ['comps', northId] });
      toast.success(currentlyVisible ? 'Hidden from report' : 'Visible on report');
    },
    onError: (error: Error) => {
      logger.error('Error toggling visibility:', error);
      toast.error(error.message || 'Failed to update visibility');
    },
  });

  const handleToggleVisibility = async (checked: boolean) => {
    try {
      const { error } = await supabase
        .from('norths')
        .update({ show_comps_on_report: checked })
        .eq('id', northId);

      if (error) throw error;
      setShowOnReport(checked);
      toast.success(checked ? "Shown on report" : "Hidden from report", {
        description: checked 
          ? "Comps will appear on the published report" 
          : "Comps are hidden from the published report",
      });
    } catch (error) {
      logger.error('Error updating visibility:', error);
      toast.error("Failed to update visibility setting");
    }
  };

  const handleRefresh = async () => {
    toast.info('Fetching similar properties from Zillow...');
    
    try {
      // First, get the north data to get the zillow_url
      const { data: northData, error: northError } = await supabase
        .from('norths')
        .select('zillow_url')
        .eq('id', northId)
        .single();

      if (northError) throw northError;
      
      if (!northData.zillow_url) {
        toast.error('No Zillow URL found for this listing');
        return;
      }

      // Call the edge function to fetch similar properties
      const { data, error } = await supabase.functions.invoke('fetch-zillow-comps', {
        body: { 
          zillowUrl: northData.zillow_url,
          northId: northId 
        }
      });

      if (error) throw error;

      if (data.success) {
        toast.success(`Fetched ${data.count} comparable properties`);
        queryClient.invalidateQueries({ queryKey: ['comps', northId] });
        
        // Small delay to ensure database write is fully propagated
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Notify parent component to refetch north data (for preview update)
        if (onUpdate) {
          await onUpdate();
        }
      } else {
        toast.error('Failed to fetch comparable properties');
      }
    } catch (error) {
      logger.error('Error refreshing comps:', error);
      toast.error('Failed to refresh comparable properties');
    }
  };

  const formatPrice = (price: number | null) => {
    if (!price) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatNumber = (num: number | null) => {
    if (!num) return '-';
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatStatus = (status: string | null) => {
    if (!status) return '-';
    // Convert FOR_SALE to "For Sale", PENDING to "Pending", etc.
    return status
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getStatusBadgeColor = (status: string | null): string => {
    if (!status) return '#374151'; // Gray-700 for unknown
    const statusLower = status.toLowerCase();
    
    // Green for sold properties
    if (statusLower.includes('sold') || statusLower === 'sold') {
      return '#15803d'; // Green-700
    }
    
    // Blue for for sale/active listings
    if (statusLower.includes('sale') || statusLower.includes('active') || statusLower === 'for_sale') {
      return '#1d4ed8'; // Blue-700
    }
    
    // Orange/amber for pending
    if (statusLower.includes('pending')) {
      return '#b45309'; // Amber-700
    }
    
    // Default gray for other statuses
    return '#374151'; // Gray-700
  };

  const formatLastUpdated = (date: string | null) => {
    if (!date) return "Never";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "Never";
    const now = new Date();
    const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    return `${Math.floor(diff / 86400)} days ago`;
  };

  // Generate summary mutation
  const generateSummary = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('generate-comps-summary', {
        body: { northId }
      });

      if (error) throw error;
      if (!data?.summary) throw new Error('No summary generated');

      return data.summary;
    },
    onSuccess: async (summary) => {
      setCompsSummary(summary);

      // Save to database
      const { error } = await supabase
        .from('norths')
        .update({ comps_summary: summary })
        .eq('id', northId);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['comps', northId] });
      queryClient.invalidateQueries({ queryKey: ['ai-summaries', northId] });

      // Small delay to ensure database write is fully propagated
      await new Promise(resolve => setTimeout(resolve, 100));

      // Notify parent component to refetch north data (for preview update)
      if (onUpdate) {
        await onUpdate();
      }

      toast.success("Summary Generated", {
        description: "Comps summary has been generated successfully",
      });
    },
    onError: (error: any) => {
      logger.error('Error generating summary:', error);
      toast.error("Error", {
        description: error.message || "Failed to generate summary",
      });
    },
  });

  // Save summary mutation
  const saveSummary = useMutation({
    mutationFn: async (summary: string) => {
      const { error } = await supabase
        .from('norths')
        .update({ comps_summary: summary })
        .eq('id', northId);

      if (error) throw error;
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['comps', northId] });
      queryClient.invalidateQueries({ queryKey: ['ai-summaries', northId] });

      // Small delay to ensure database write is fully propagated
      await new Promise(resolve => setTimeout(resolve, 100));

      // Notify parent component to refetch north data (for preview update)
      if (onUpdate) {
        await onUpdate();
      }

      toast.success("Summary Saved", {
        description: "Comps summary has been saved successfully",
      });
    },
    onError: (error: any) => {
      logger.error('Error saving summary:', error);
      toast.error("Error", {
        description: "Failed to save summary",
      });
    },
  });

  const handleGenerateSummary = () => {
    setIsGeneratingSummary(true);
    generateSummary.mutate(undefined, {
      onSettled: () => setIsGeneratingSummary(false)
    });
  };

  const handleSaveSummary = () => {
    saveSummary.mutate(compsSummary);
  };

  if (isLoading) {
    return <div className="p-6">Loading comparables...</div>;
  }

  return (
    <div className="space-y-4">
      {isPublished && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            This report is published. Click "Unpublish" to make changes.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {/* Title and Description */}
        <div>
          <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2">
            Comparable Properties
            <span className="text-sm md:text-base text-muted-foreground ml-3">
              ({comps?.length || 0} total • {comps?.filter(c => c.visible).length || 0}/5 visible)
            </span>
          </h3>
          <p className="text-sm md:text-base text-muted-foreground">
            Find and manage similar properties to help your seller understand their home's market value
          </p>
        </div>

        {/* Switch and Actions Row */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              id="show-on-report"
              checked={showOnReport}
              onCheckedChange={handleToggleVisibility}
              disabled={isPublished}
              style={showOnReport ? { backgroundColor: '#22c55e' } : undefined}
            />
            <Label htmlFor="show-on-report" className="text-sm font-medium cursor-pointer whitespace-nowrap">
              Show on Report
            </Label>
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsSettingsOpen(true)}
            disabled={isPublished}
            className="flex-shrink-0"
            style={{ borderColor: secondaryColor || '#1b489b', color: secondaryColor || '#1b489b' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = secondaryColor || '#1b489b';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = secondaryColor || '#1b489b';
            }}
          >
            <Settings className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Comp Settings</span>
          </Button>
          <Button
            size="sm"
            onClick={handleRefresh}
            disabled={isPublished}
            className="flex-1 sm:flex-none"
            style={{ backgroundColor: secondaryColor || '#1b489b', color: getContrastColor(secondaryColor || '#1b489b') }}
          >
            <RefreshCw className="h-4 w-4 md:mr-2" />
            <span className="hidden sm:inline">Fetch Similar Properties</span>
            <span className="sm:hidden">Fetch Comps</span>
          </Button>
          <Button
            size="sm"
            onClick={() => setIsAddDialogOpen(true)}
            disabled={isPublished}
            className="flex-shrink-0"
            style={{ backgroundColor: secondaryColor || '#1b489b', color: getContrastColor(secondaryColor || '#1b489b') }}
          >
            + Add Comp
          </Button>
          </div>
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Property</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Price</TableHead>
              <TableHead className="text-right">SQ FT</TableHead>
              <TableHead className="text-right">$/SQ FT</TableHead>
              <TableHead className="text-center">Bed/Bath</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {comps?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No comparable properties yet. Add some to get started.
                </TableCell>
              </TableRow>
            ) : (
              comps?.map((comp) => {
                const violations = getCompViolations(comp);
                const hasViolations = violations.length > 0;

                return (
                <TableRow key={comp.id} className={hasViolations ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{comp.address}</div>
                      <div className="text-sm text-muted-foreground">
                        {comp.city}, {comp.state} {comp.zip}
                      </div>
                      {hasViolations && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {violations.map((violation, idx) => (
                            <Badge
                              key={idx}
                              variant="outline"
                              className="text-[9px] px-1.5 py-0 h-4 bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700"
                            >
                              ⚠️ {violation}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {comp.status ? (
                      <Badge
                        variant="secondary"
                        style={{ 
                          backgroundColor: getStatusBadgeColor(comp.status), 
                          color: 'white', 
                          borderColor: 'transparent' 
                        }}
                      >
                        {formatStatus(comp.status)}
                      </Badge>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatPrice(comp.price)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatNumber(comp.square_feet)}
                  </TableCell>
                  <TableCell className="text-right">
                    {comp.price_per_sqft
                      ? `$${formatNumber(comp.price_per_sqft)}`
                      : '-'}
                  </TableCell>
                  <TableCell className="text-center">
                    {comp.bedrooms && comp.bathrooms
                      ? `${comp.bedrooms}/${comp.bathrooms}`
                      : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      {comp.zillow_url && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => window.open(comp.zillow_url, '_blank', 'noopener,noreferrer')}
                          title="View on Zillow"
                        >
                          <ZillowIcon className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`h-8 w-8 ${comp.visible ? 'text-green-600 hover:text-green-700' : 'text-destructive hover:text-destructive/80'}`}
                        onClick={() => toggleVisibility.mutate({ compId: comp.id, currentlyVisible: comp.visible })}
                        disabled={isPublished}
                        title={comp.visible ? 'Hide from report' : 'Show on report'}
                      >
                        {comp.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => deleteComp.mutate(comp.id)}
                        disabled={isPublished}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {comps?.length === 0 ? (
          <div className="text-center py-12 bg-muted/20 rounded-lg">
            <p className="text-muted-foreground mb-2">No comparable properties yet</p>
            <p className="text-sm text-muted-foreground">Add some to get started</p>
          </div>
        ) : (
          comps?.map((comp) => {
            const violations = getCompViolations(comp);
            const hasViolations = violations.length > 0;

            return (
              <div key={comp.id} className={`border rounded-lg p-3 bg-card ${hasViolations ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}`}>
                <div className="mb-3">
                  <div className="font-medium text-sm text-foreground mb-1 break-words">{comp.address}</div>
                  <div className="text-xs text-muted-foreground mb-2 break-words">
                    {comp.city}, {comp.state} {comp.zip}
                  </div>
                  {hasViolations && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {violations.map((violation, idx) => (
                        <Badge
                          key={idx}
                          variant="outline"
                          className="text-[9px] px-1.5 py-0 h-4 bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-700"
                        >
                          ⚠️ {violation}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="text-muted-foreground text-[10px] uppercase">Status:</span>
                      {comp.status ? (
                        <Badge
                          variant="secondary"
                          className="text-[10px] h-4 px-1.5"
                          style={{ 
                            backgroundColor: getStatusBadgeColor(comp.status), 
                            color: 'white', 
                            borderColor: 'transparent' 
                          }}
                        >
                          {formatStatus(comp.status)}
                        </Badge>
                      ) : (
                        <span>-</span>
                      )}
                    </div>
                    <div className="truncate">
                      <span className="text-muted-foreground text-[10px] uppercase">Price:</span>{' '}
                      <span className="font-medium">${comp.price?.toLocaleString()}</span>
                    </div>
                    <div className="truncate">
                      <span className="text-muted-foreground text-[10px] uppercase">Sq Ft:</span>{' '}
                      <span>{comp.square_feet?.toLocaleString()}</span>
                    </div>
                    <div className="truncate">
                      <span className="text-muted-foreground text-[10px] uppercase">$/Sq Ft:</span>{' '}
                      <span>{comp.price_per_sqft ? `$${comp.price_per_sqft}` : 'N/A'}</span>
                    </div>
                    <div className="truncate">
                      <span className="text-muted-foreground text-[10px] uppercase">Bed/Bath:</span>{' '}
                      <span>{comp.bedrooms || 'N/A'} / {comp.bathrooms || 'N/A'}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2 pt-3 border-t">
                  <div className="flex flex-wrap gap-1.5">
                    {comp.zillow_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-xs"
                        onClick={() => window.open(comp.zillow_url, '_blank', 'noopener,noreferrer')}
                      >
                        <ZillowIcon className="h-3.5 w-3.5 mr-1" />
                        <span className="hidden xs:inline">Zillow</span>
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-8 px-2 text-xs ${comp.visible ? 'text-green-600 hover:text-green-700' : 'text-destructive hover:text-destructive/80'}`}
                      onClick={() => toggleVisibility.mutate({ compId: comp.id, currentlyVisible: comp.visible })}
                      disabled={isPublished}
                    >
                      {comp.visible ? <Eye className="h-3.5 w-3.5 mr-1" /> : <EyeOff className="h-3.5 w-3.5 mr-1" />}
                      <span className="hidden xs:inline">{comp.visible ? 'Hide' : 'Show'}</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-xs text-destructive"
                      onClick={() => deleteComp.mutate(comp.id)}
                      disabled={isPublished}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" />
                      <span className="hidden xs:inline">Delete</span>
                    </Button>
                  </div>
                  <Badge
                    variant={comp.visible ? 'default' : 'secondary'}
                    className={`text-[10px] h-5 px-2 flex-shrink-0 ${comp.visible ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700 text-white'}`}
                  >
                    {comp.visible ? 'Shown' : 'Hidden'}
                  </Badge>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* AI Summary Section */}
      <div className="mt-8 border-t pt-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-1">AI Summary</h3>
          <p className="text-sm text-muted-foreground">
            Generate an AI-powered summary analyzing your comparable properties and market positioning
          </p>
        </div>

        <RichTextEditor
          value={compsSummary}
          onChange={setCompsSummary}
          onSave={(summary) => saveSummary.mutate(summary)}
          placeholder="Click 'Generate Summary' to create an AI summary of your comps..."
          disabled={isPublished}
          className="mb-4"
        />

        <div className="flex gap-2">
          <Button
            onClick={handleGenerateSummary}
            disabled={isGeneratingSummary || isPublished || (comps?.length || 0) === 0}
            style={{ backgroundColor: secondaryColor || '#1b489b', color: getContrastColor(secondaryColor || '#1b489b') }}
          >
            <Sparkles className={`h-4 w-4 mr-2 ${isGeneratingSummary ? 'animate-spin' : ''}`} />
            {isGeneratingSummary ? 'Generating...' : 'Generate Summary'}
          </Button>
          <Button
            onClick={handleSaveSummary}
            variant="outline"
            disabled={!compsSummary || isPublished || saveSummary.isPending}
            style={{ borderColor: secondaryColor || '#1b489b', color: secondaryColor || '#1b489b' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = secondaryColor || '#1b489b';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = secondaryColor || '#1b489b';
            }}
          >
            {saveSummary.isPending ? 'Saving...' : 'Save Summary'}
          </Button>
        </div>

        {compsSummary && (
          <p className="text-xs text-muted-foreground mt-2">
            This summary also appears in the AI Summaries menu on the report.
          </p>
        )}

        {(comps?.length || 0) === 0 && (
          <p className="text-sm text-muted-foreground mt-2">
            Add comparable properties before generating a summary
          </p>
        )}
      </div>

      <AddCompDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        northId={northId}
      />

      <CompSettingsDialog
        open={isSettingsOpen}
        onOpenChange={setIsSettingsOpen}
        northId={northId}
        northAddress={northAddress}
      />
    </div>
  );
};
